/**
 * @file dialog-modal.component.tsx
 * @description Компонент диалогового модального окна для Task Manager приложения, который обеспечивает интерфейс подтверждения пользовательских действий с настраиваемыми сообщениями, заголовками и кнопками действий. Поддерживает различные режимы отображения включая опциональную кнопку отмены, кастомные лейблы для кнопок подтверждения и отклонения, обработку событий клавиатуры и клика вне области модального окна, интеграцию с системой управления модальными окнами через useModalQueue.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-16
 * @dependencies ['@consta/uikit/Modal', '../modal/components', '@hooks/use-modals', '@consta/uikit/Text', 'classnames', '@components/ui/base/button']
 * @related-files ['src/components/ui/layout/modal/components/header.component.tsx', 'src/hooks/use-modals.ts', 'src/components/ui/button']
 */

import { Modal } from '@consta/uikit/Modal';
import { Header } from '../modal/components';
import { useModalQueue } from '@hooks/use-modals';
import { Text } from '@consta/uikit/Text';
import cn from 'classnames';
import { Button } from '@components/ui/base/button';

/**
 * Props для компонента диалогового модального окна
 */
interface IDialogModalProps {
    /** HTML контейнер для рендеринга модального окна */
    container?: HTMLDivElement;
    /** Состояние видимости модального окна */
    isShow: boolean;
    /** Функция для управления видимостью модального окна */
    setIsShow: (isShow: boolean) => void;
    /** Текст сообщения в диалоге */
    message?: string;
    /** Заголовок модального окна */
    title: string;
    /** CSS класс для дополнительной стилизации */
    className?: string;
    /** Обработчик подтверждения действия */
    onConfirm?: () => void;
    /** Обработчик отклонения действия */
    onReject?: () => void;
    /** Показывать ли кнопку "Отмена" */
    isWithCancel?: boolean;
    /** Текст кнопки подтверждения */
    confirmLabel?: string;
    /** Текст кнопки отклонения */
    rejectLabel?: string;
}

export const DialogModal = ({
    container,
    isShow,
    setIsShow,
    message,
    title,
    className,
    onConfirm,
    onReject,
    isWithCancel,
    confirmLabel = 'Да',
    rejectLabel = 'Нет'
}: IDialogModalProps) => {
    const closeModalHandler = useModalQueue(setIsShow);

    return (
        <Modal
            isOpen={isShow}
            hasOverlay={false}
            onClickOutside={(e: MouseEvent) => {
                if (e.target && !(e.target as Element).closest('.dhx_popup')) {
                    closeModalHandler();
                }
            }}
            position="center"
            onEsc={closeModalHandler}
            className={cn('dialog-modal grid w-[60.0179vmin] min-h-[17.9952vmin]', className)}
            container={container}
        >
            <div className="dialog-modal_container flex flex-col h-full p-[3.1756vmin] ">
                <Header title={title} closeBtnClickHandler={() => closeModalHandler()} />

                {message && (
                    <div className="flex flex-row mb-[1.5878vmin] flex-1 h-full">
                        <Text view="primary" className="leading-[2.2187vmin]" size="m">
                            {message}
                        </Text>
                    </div>
                )}

                <div className="flex flex-row w-full justify-between items-end flex-1 h-full">
                    {isWithCancel && (
                        <Button
                            className="text-[1.482vmin]"
                            label="Отмена"
                            onClick={() => closeModalHandler()}
                            view="clear"
                        />
                    )}

                    <div>
                        <Button
                            className="mr-[0.8468vmin] text-[1.482vmin]"
                            label={confirmLabel}
                            onClick={() => {
                                onConfirm && onConfirm();
                                closeModalHandler();
                            }}
                            view="primary"
                        />

                        <Button
                            className="text-[1.482vmin]"
                            label={rejectLabel}
                            onClick={() => {
                                onReject && onReject();
                                closeModalHandler();
                            }}
                            view="ghost"
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};